// URI scheme for the synthesized read-only models that back Monaco's native
// "Peek References" / "Go to References" widget.
//
// Cross-object references live in OTHER persisted objects (view / routine /
// trigger / constraint bodies), so Monaco needs a resolvable `Uri` per
// referencing object before it can render a multi-file peek. We mint one
// stable URI per (connection, database, object) and register a matching
// in-memory `ITextModel` under it — Monaco standalone's
// `StandaloneTextModelService` resolves peek locations straight out of the
// model registry, so a model that exists when the provider returns is all the
// peek widget needs.
//
// Shape: `jamsql-ref:/<connectionId>/<database>/<objectKind>/<schema>/<name>.sql`
// (no authority — Monaco lowercases the authority when it re-serialises a
// `Uri`, which would corrupt case-sensitive connection ids). The peek tree
// renders the basename (`open_orders.sql`) with the rest of the path as its
// description, so the layout doubles as the user-visible label.

import type { ObjectKind, QualifiedName } from '../types';

export const PEEK_REFERENCE_SCHEME = 'jamsql-ref';

/** Placeholder path segment for objects with no schema (SQLite views, MySQL
 *  objects addressed without a schema qualifier). */
const NO_SCHEMA_SEGMENT = '_';

const SQL_SUFFIX = '.sql';

/** Everything the peek surface needs to describe one referencing object. */
export type PeekReferenceTarget = {
  readonly connectionId: string;
  readonly database: string;
  readonly objectKind: ObjectKind;
  readonly schema: string | null;
  readonly name: string;
};

/** Build the URI string for a referencing object. Pass the result through
 *  `monaco.Uri.parse(...)` and use the parsed `Uri.toString()` as the cache
 *  key — Monaco's own encoder differs slightly from `encodeURIComponent`, so
 *  only the round-tripped form is guaranteed to match `model.uri.toString()`. */
export function buildPeekReferenceUri(target: PeekReferenceTarget): string {
  const segments = [
    target.connectionId,
    target.database || 'default',
    target.objectKind,
    target.schema && target.schema.length > 0 ? target.schema : NO_SCHEMA_SEGMENT,
    `${target.name}${SQL_SUFFIX}`,
  ];
  return `${PEEK_REFERENCE_SCHEME}:/${segments.map(encodeURIComponent).join('/')}`;
}

/** Convenience wrapper for the common "I have a group header" call shape. */
export function peekReferenceTargetFor(args: {
  readonly connectionId: string;
  readonly database: string;
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
}): PeekReferenceTarget {
  return {
    connectionId: args.connectionId,
    database: args.database,
    objectKind: args.objectKind,
    schema: args.objectName.schema ?? null,
    name: args.objectName.name,
  };
}

/** True when `scheme` belongs to a synthesized peek-reference model. Used by
 *  the read-only guard + the editor opener to recognise "not a real buffer". */
export function isPeekReferenceScheme(scheme: string | undefined): boolean {
  return scheme === PEEK_REFERENCE_SCHEME;
}

/** Inverse of `buildPeekReferenceUri`. `path` is the already-percent-decoded
 *  path Monaco exposes on `Uri.path`; raw (still-encoded) paths are accepted
 *  too because each segment is decoded defensively. Returns `null` for any URI
 *  that is not ours. */
export function parsePeekReferenceUri(uri: {
  readonly scheme?: string;
  readonly path?: string;
}): PeekReferenceTarget | null {
  if (!isPeekReferenceScheme(uri.scheme)) return null;
  const raw = uri.path ?? '';
  const segments = raw.split('/').filter((s) => s.length > 0).map(decodeSegment);
  if (segments.length !== 5) return null;
  const [connectionId, database, objectKind, schema, file] = segments;
  if (!file.endsWith(SQL_SUFFIX)) return null;
  return {
    connectionId,
    database,
    objectKind: objectKind as ObjectKind,
    schema: schema === NO_SCHEMA_SEGMENT ? null : schema,
    name: file.slice(0, -SQL_SUFFIX.length),
  };
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}
